// Direct-to-GCS (prod) or server-local (dev Docker) upload for web portals.
//
// Prod (GCS_BUCKET set):
//   1. POST /api/uploads/presign → { mode:'gcs', uploadUrl, publicUrl, ... }
//   2. Compress images in-browser, PUT bytes to GCS
//
// Local dev (no GCS):
//   1. POST /api/uploads/presign → { mode:'direct', uploadUrl, method:'POST', ... }
//   2. POST multipart to /api/uploads/direct (server compresses with sharp)

import { getAuthBaseUrl, getToken } from './authApi';
import { getOrganizerToken } from './organizerApi';
import { prepareUploadFile } from './uploadCompress';

/** Prefer the organizer token, fall back to the admin token. */
export function portalBearer() {
  return getOrganizerToken() || getToken() || null;
}

const VIDEO_TYPES = ['video/mp4', 'video/quicktime'];

/**
 * Upload one image or short video and return its public URL + media type.
 * @returns {Promise<{ success: boolean, url?: string, type?: 'image'|'video', error?: string }>}
 */
export async function uploadMedia(file, { kind = 'event' } = {}) {
  const base = getAuthBaseUrl();
  if (!base) return { success: false, error: 'API base not configured' };
  const token = portalBearer();
  if (!token) return { success: false, error: 'not_authenticated' };

  let uploadFile = file;
  const rawType = String(file?.type || '').toLowerCase();
  const isVideo = VIDEO_TYPES.includes(rawType);
  if (!isVideo) {
    uploadFile = await prepareUploadFile(file, kind);
  }
  const contentType = String(uploadFile?.type || rawType || '').toLowerCase();
  const isVideoFinal = VIDEO_TYPES.includes(contentType);
  if (!isVideoFinal && !['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'].includes(contentType)) {
    return { success: false, error: 'unsupported_content_type' };
  }

  let presign;
  try {
    const res = await fetch(`${base}/api/uploads/presign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ kind, contentType, contentLength: uploadFile.size }),
    });
    presign = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: presign.error || `presign_failed_${res.status}` };
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'network_error' };
  }

  if (presign.mode === 'direct') {
    try {
      const form = new FormData();
      form.append('file', uploadFile);
      form.append('kind', kind);
      form.append('contentType', contentType);
      const directUrl = presign.uploadUrl || `${base}/api/uploads/direct`;
      const res = await fetch(directUrl, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return { success: false, error: data.error || `upload_failed_${res.status}` };
      return {
        success: true,
        url: data.publicUrl,
        type: isVideoFinal ? 'video' : 'image',
      };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'network_error' };
    }
  }

  try {
    const put = await fetch(presign.uploadUrl, {
      method: 'PUT',
      headers: presign.headers || { 'Content-Type': contentType },
      body: uploadFile,
    });
    if (!put.ok) return { success: false, error: `upload_failed_${put.status}` };
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'network_error' };
  }

  return { success: true, url: presign.publicUrl, type: isVideoFinal ? 'video' : 'image' };
}

/**
 * Upload one image/SVG file and return its public URL.
 * @returns {Promise<{ success: boolean, url?: string, error?: string }>}
 */
export async function uploadImage(file, { kind = 'badge' } = {}) {
  const out = await uploadMedia(file, { kind });
  if (!out.success) return out;
  return { success: true, url: out.url };
}
